import { Link, useLocation } from "react-router-dom";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ReLoginMethodNotice } from "../components/ReLoginMethodNotice";
import { useAccountState } from "../hooks/useAccountState";
import { api, type AccountState, type InviteApplyErrorCode } from "../lib/api";
import { AppError, toUserMessage } from "../lib/errors";
import { hasActiveEntitlement } from "../lib/featureAccess";
import { logoutToSplash } from "../lib/logout";
import {
  clearPendingStartInvite,
  readPendingStartInvite,
  writePendingStartInvite,
  type PendingStartInvite,
} from "../lib/startInviteSession";

type StartProvider = "line" | "web" | null;
type InviteApplyStatus = "idle" | "applying" | "applied" | "alreadyApplied" | "failed";

const inviteErrorMessages: Record<InviteApplyErrorCode, string> = {
  invalid_invite_code: "招待コードが見つかりません。入力内容を確認してください。",
  invite_code_not_started: "この招待コードはまだ利用開始前です。",
  invite_code_expired: "招待コードの有効期限が切れています。",
  invite_code_usage_limit_exceeded: "この招待コードは利用上限に達しています。",
  invite_code_inactive: "この招待コードは現在利用できません。",
};

const inviteApplyStatusTitles: Record<InviteApplyStatus, string> = {
  idle: "",
  applying: "招待コードを確認しています",
  applied: "招待コードを適用しました",
  alreadyApplied: "招待コードは適用済みです",
  failed: "招待コードを適用できませんでした",
};

function resolveProvider(value: string | null): StartProvider {
  return value === "line" || value === "web" ? value : null;
}

function isInviteApplyErrorCode(value: unknown): value is InviteApplyErrorCode {
  return (
    value === "invalid_invite_code" ||
    value === "invite_code_not_started" ||
    value === "invite_code_expired" ||
    value === "invite_code_usage_limit_exceeded" ||
    value === "invite_code_inactive"
  );
}

function extractInviteApplyErrorCode(error: unknown): InviteApplyErrorCode | null {
  if (!(error instanceof AppError) || !error.meta.responseText) return null;

  try {
    const parsed = JSON.parse(error.meta.responseText) as {
      errorCode?: unknown;
      error_code?: unknown;
      data?: { errorCode?: unknown; error_code?: unknown };
    };
    const errorCode = parsed.errorCode ?? parsed.error_code ?? parsed.data?.errorCode ?? parsed.data?.error_code;
    return isInviteApplyErrorCode(errorCode) ? errorCode : null;
  } catch {
    return null;
  }
}

export default function StartPage() {
  const location = useLocation();
  const { accountState, loading, error } = useAccountState();
  const [pendingInvite, setPendingInvite] = useState<PendingStartInvite | null>(() => readPendingStartInvite());
  const [resumedAccountState, setResumedAccountState] = useState<AccountState | null>(null);
  const [inviteApplyStatus, setInviteApplyStatus] = useState<InviteApplyStatus>("idle");
  const [inviteApplyMessage, setInviteApplyMessage] = useState<string | null>(null);
  const [inviteInput, setInviteInput] = useState(() => readPendingStartInvite()?.inviteCode ?? "");
  const [inviteInputMessage, setInviteInputMessage] = useState<string | null>(null);
  const resumeInFlightRef = useRef(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const inviteParam = searchParams.get("invite");
  const provider = resolveProvider(searchParams.get("provider"));
  const sourcePath = `${location.pathname}${location.search}`;
  const displayAccountState = resumedAccountState ?? accountState;

  useEffect(() => {
    if (!inviteParam) return;
    const saved = writePendingStartInvite({ inviteCode: inviteParam, sourcePath });
    if (saved) {
      setPendingInvite(saved);
      setInviteInput(saved.inviteCode);
      setInviteInputMessage("この招待コードで開始します。");
    }
  }, [inviteParam, sourcePath]);

  const handleInviteSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const inviteCode = inviteInput.trim();
      if (!inviteCode) {
        setInviteInputMessage("招待コードを入力してください。");
        return;
      }

      const saved = writePendingStartInvite({ inviteCode, sourcePath });
      if (!saved) {
        setInviteInputMessage("招待コードを保存できませんでした。ページを読み直して、もう一度お試しください。");
        return;
      }

      setPendingInvite(saved);
      setInviteInput(saved.inviteCode);
      setInviteInputMessage("この招待コードで開始します。");
      setInviteApplyStatus("idle");
      setInviteApplyMessage(null);
    },
    [inviteInput, sourcePath]
  );

  useEffect(() => {
    if (loading || error || !pendingInvite || resumeInFlightRef.current) return;
    if (accountState?.authStatus !== "authenticated") return;

    let active = true;
    resumeInFlightRef.current = true;
    setInviteApplyStatus("applying");
    setInviteApplyMessage(null);

    (async () => {
      try {
        const result = await api.applyInviteCode(pendingInvite.inviteCode);
        if (!active) return;

        setResumedAccountState(result.accountState);
        if (result.errorCode) {
          setInviteApplyStatus("failed");
          setInviteApplyMessage(inviteErrorMessages[result.errorCode]);
        } else if (result.alreadyApplied) {
          setInviteApplyStatus("alreadyApplied");
          setInviteApplyMessage("この招待コードはすでに適用済みです。");
        } else {
          setInviteApplyStatus("applied");
          setInviteApplyMessage("WizMateを利用できるようになりました。");
        }
      } catch (e) {
        if (!active) return;

        const errorCode = extractInviteApplyErrorCode(e);
        setInviteApplyStatus("failed");
        setInviteApplyMessage(errorCode ? inviteErrorMessages[errorCode] : toUserMessage(e));
      } finally {
        clearPendingStartInvite();
        if (active) {
          setPendingInvite(null);
          resumeInFlightRef.current = false;
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [accountState, error, loading, pendingInvite]);

  const isAuthenticated = displayAccountState?.authStatus === "authenticated";
  const shouldShowReLoginNotice = isAuthenticated && !hasActiveEntitlement(displayAccountState);
  const shouldShowInviteApplyNotice = inviteApplyStatus !== "idle" || Boolean(inviteApplyMessage);

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#FDF8F5] px-6 text-[#4A3E39] antialiased">
      <section className="w-full max-w-md rounded-[2rem] border border-[#EADBC8] bg-white/85 p-7 shadow-[0_18px_40px_rgba(166,124,82,0.14)]">
        <p className="text-[11px] font-black tracking-[0.16em] text-[#A67C52]">WIZMATE START</p>
        <h1 className="mt-2 text-3xl font-black tracking-tight text-[#7C5A4E]">WizMateをはじめる</h1>
        <p className="mt-3 text-sm leading-6 text-[#6B5851]">
          招待コードを入力して、WizMateをはじめましょう。
        </p>

        <form className="mt-6 rounded-[1.2rem] border border-[#EADBC8] bg-[#FFF9F4] px-4 py-4 text-sm" onSubmit={handleInviteSubmit}>
          <label htmlFor="start-invite-code" className="font-bold text-[#5D4D46]">
            招待コード
          </label>
          <p id="start-invite-code-help" className="mt-1 text-xs leading-5 text-[#7B6861]">
            お持ちの招待コードを入力してください。
          </p>
          <input
            id="start-invite-code"
            value={inviteInput}
            onChange={(event) => {
              setInviteInput(event.target.value);
              setInviteInputMessage(null);
            }}
            aria-describedby="start-invite-code-help"
            autoComplete="off"
            className="mt-3 w-full rounded-xl border border-[#D9C4AF] bg-white px-3 py-3 text-sm font-bold text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#EADBC8]"
            placeholder="招待コードを入力"
          />
          <button
            type="submit"
            className="mt-3 w-full rounded-xl bg-[#7C5A4E] px-4 py-3 text-sm font-black text-white transition hover:bg-[#6A4D43] active:scale-[0.99]"
          >
            このコードで始める
          </button>
          {inviteInputMessage ? (
            <p className="mt-3 text-xs font-bold text-[#7B6861]" aria-live="polite">
              {inviteInputMessage}
            </p>
          ) : null}
        </form>

        <div className="mt-4 rounded-[1.2rem] border border-[#EADBC8] bg-[#FFF9F4] px-4 py-3 text-sm">
          <p className="font-bold text-[#5D4D46]">適用予定の招待コード</p>
          <p className="mt-1 break-all text-xs text-[#7B6861]">
            {pendingInvite ? pendingInvite.inviteCode : "まだありません"}
          </p>
          {shouldShowInviteApplyNotice ? (
            <div className="mt-3 rounded-xl bg-white px-3 py-3" aria-live="polite">
              <p className="text-xs font-black text-[#5D4D46]">{inviteApplyStatusTitles[inviteApplyStatus]}</p>
              {inviteApplyMessage ? (
                <p className="mt-1 text-xs font-bold text-[#7B6861]">{inviteApplyMessage}</p>
              ) : null}
            </div>
          ) : null}
        </div>

        {provider ? (
          <p className="mt-3 rounded-[1rem] bg-[#F5EEE8] px-4 py-2 text-xs font-bold text-[#7B6861]">
            選択中のログイン方法: {provider === "line" ? "LINE" : "Web"}
          </p>
        ) : null}

        {loading ? <p className="mt-6 text-sm font-bold text-[#7B6861]">利用状態を確認しています...</p> : null}
        {!loading && error ? <p className="mt-6 text-sm font-bold text-[#A1493E]">{error}</p> : null}

        {!loading && !error && !isAuthenticated ? (
          <div className="mt-7 space-y-3" aria-label="authentication options">
            <p className="text-sm font-bold text-[#5D4D46]">ログイン方法を選択</p>
            <p className="text-xs leading-5 text-[#7B6861]">
              LINEで始めた方は「LINEで続ける」、Googleで始めた方は「Webで続ける」からGoogleログインを選んでください。
            </p>
            <Link
              to="/line-login"
              className="block rounded-2xl bg-[#06C755] px-4 py-3 text-center text-sm font-black text-white transition hover:bg-[#05b34d]"
            >
              LINEで続ける
            </Link>
            <Link
              to="/splash"
              className="block rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-center text-sm font-black text-[#5D4D46] transition hover:bg-[#FDF8F5]"
            >
              Webで続ける
            </Link>
          </div>
        ) : null}

        {!loading && !error && isAuthenticated && displayAccountState ? (
          <div className="mt-7 rounded-[1.2rem] border border-[#EADBC8] bg-white px-4 py-4 text-sm">
            <p className="font-black text-[#5D4D46]">現在の利用状態</p>
            <dl className="mt-3 grid grid-cols-[7rem_1fr] gap-x-3 gap-y-2 text-xs text-[#6B5851]">
              <dt className="font-bold">ログイン状態</dt>
              <dd>{displayAccountState.authStatus}</dd>
              <dt className="font-bold">プロファイル</dt>
              <dd>{displayAccountState.profileStatus}</dd>
              <dt className="font-bold">プラン</dt>
              <dd>{displayAccountState.plan}</dd>
              <dt className="font-bold">プランコード</dt>
              <dd>{displayAccountState.planCode ?? "none"}</dd>
              <dt className="font-bold">利用状態</dt>
              <dd>{displayAccountState.entitlementStatus}</dd>
              <dt className="font-bold">使える機能</dt>
              <dd>{displayAccountState.featureScope.length > 0 ? displayAccountState.featureScope.join(", ") : "none"}</dd>
            </dl>
            {shouldShowReLoginNotice ? (
              <ReLoginMethodNotice onLogout={logoutToSplash} className="mt-4" />
            ) : null}
          </div>
        ) : null}
      </section>
    </main>
  );
}
